// src/services/sseClient.ts
import * as Sentry from '@sentry/vue';

export default class SSEClient {
  url: string;
  bounce: string;

  // the EventSource handle
  eventSource: EventSource | null = null;
  // hold the top‐level transaction
  RECONNECTION_TIMEOUT = 5000;
  MAX_RECONNECTION_TIMEOUT = 30000;
  currentReconnectionTimeout = this.RECONNECTION_TIMEOUT;

  constructor(url: string, bounce: string) {
    this.url = url;
    this.bounce = bounce;
  }

  onMessage?: (event: MessageEvent) => void;
  onDisconnected?: () => void;
  onConnected?: () => void;

  connect() {
    if (this.onConnected) {
      this.onConnected();
    }

    // 1. Start a new Sentry transaction for this SSE connection
    const traceData = Sentry.getTraceData();

    // 2. Get trace headers from the transaction
    const sentryTraceHeader = traceData['sentry-trace'];
    const sentryBaggageHeader = traceData['baggage'];

    // 3. Build URL with trace context
    const fullUrl =
      `${this.url}?bounce_id=${this.bounce}` +
      `&sentry_trace=${encodeURIComponent(sentryTraceHeader || '')}` +
      `&sentry_baggage=${encodeURIComponent(sentryBaggageHeader || '')}`;

    // 4. Open the SSE stream
    this.eventSource = new EventSource(fullUrl, { withCredentials: true });

    if (this.eventSource) {
      // 5. Wrap each incoming message in a child span
      this.eventSource.onmessage = (event: MessageEvent) => {
        if (this.onMessage) this.onMessage(event);
      };

      // 6. When the server signals “disconnected”, finish the transaction
      this.eventSource.addEventListener('disconnected', () => {
        this.closeConnection();
      });

      // 7. On error, finish and retry
      this.eventSource.onerror = () => {
        this.reconnect();
      };
    }
  }

  reconnect() {
    this.closeConnection();
    setTimeout(() => this.connect(), this.currentReconnectionTimeout);
    this.currentReconnectionTimeout = Math.min(this.currentReconnectionTimeout * 1.2, this.MAX_RECONNECTION_TIMEOUT);
  }

  closeConnection() {
    if (this.onDisconnected) this.onDisconnected();
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
